/**
 * Persona-aware dashboard bodies for service providers and storefront
 * sellers, added 2026-02-28.
 *
 * Each component:
 *   - Hits its own `/api/me/{persona}-dashboard` endpoint on mount
 *   - Shows a skeleton state while loading (no jarring layout shift)
 *   - Renders a compact 4-card stat row + an "activity" list + a CTA
 *     pointing to the deeper surface (`/provider/me` or `/me/store`)
 *   - On 404 (user has no provider profile / no store yet), shows a
 *     friendly empty state with the right onboarding CTA
 *
 * Convention: this file owns the layout but the data shape is dictated
 * by `routers/persona_dashboards.py`. Both stay aligned via the
 * documented response keys.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ScissorsIcon as Scissors, StorefrontIcon as Storefront, ChartBarIcon as ChartBar, StarIcon as Star, CurrencyDollarIcon as CurrencyDollar, WarningIcon as Warning, PackageIcon as Package, ShieldCheckIcon as ShieldCheck, PawPrintIcon as PawPrint, ArrowRightIcon as ArrowRight, CheckCircleIcon as CheckCircle, ChatCircleDotsIcon as ChatCircleDots, VideoCameraIcon as VideoCamera } from "@phosphor-icons/react";

const fmtUsd = (cents) =>
  `$${((cents || 0) / 100).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtUsdRaw = (n) =>
  `$${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// ---------------------------------------------------------------------
// Shared skeleton — used while data is loading. Avoids layout jumps.
// ---------------------------------------------------------------------
function DashboardSkeleton() {
  return (
    <div className="space-y-8 animate-pulse" data-testid="persona-dashboard-skeleton">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-2xl border border-[#EFEFEF] bg-white p-5 h-28" />
        ))}
      </div>
      <div className="rounded-2xl border border-[#EFEFEF] bg-white p-6 h-48" />
    </div>
  );
}

// ---------------------------------------------------------------------
// Stat tile — reused for both personas. Compact, gold-on-cream accent.
// ---------------------------------------------------------------------
function StatTile({ icon: IconCmp, label, value, sub, tone = "default", testId }) {
  const toneClasses =
    tone === "warn"
      ? "border-[#C1272D]/30 bg-[#FDECEC]"
      : tone === "ok"
      ? "border-[#E8D9A6] bg-[#FBF7EC]"
      : "border-[#EFEFEF] bg-white";
  return (
    <div
      className={`rounded-2xl border ${toneClasses} p-4 md:p-5 transition hover:shadow-[0_8px_20px_-12px_rgba(0,0,0,0.18)]`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-[0.18em] text-[#6B6B6B]">
        <IconCmp size={13} weight="duotone" /> {label}
      </div>
      <div className="font-spectral font-light text-3xl text-ink tracking-tight tabular-nums">
        {value}
      </div>
      {sub ? <div className="text-[12px] text-[#6B6B6B] mt-1">{sub}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------------
// Provider body
// ---------------------------------------------------------------------
export function ProviderDashboardBody() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get("/me/provider-dashboard")
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setError(e?.response?.status === 404 ? "no_profile" : "error"));
    return () => {
      alive = false;
    };
  }, []);

  if (error === "no_profile") {
    return (
      <div
        className="rounded-2xl border border-[#E8D9A6] bg-[#FBF7EC] p-8 text-center"
        data-testid="provider-empty-state"
      >
        <Scissors size={32} weight="duotone" className="mx-auto text-gold-dark mb-3" />
        <h3 className="font-spectral text-2xl text-ink mb-2">Become a service provider</h3>
        <p className="text-[14px] text-[#3F3F3F] mb-5 max-w-md mx-auto">
          You've picked Service Provider as your role, but haven't applied yet.
          List your services and start taking bookings.
        </p>
        <Link to="/become-a-provider">
          <Button className="rounded-full bg-ink hover:bg-crimson text-white px-6 h-11" data-testid="provider-apply-cta">
            Apply now <ArrowRight size={14} className="ml-1.5" />
          </Button>
        </Link>
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-sm text-[#C1272D]" data-testid="provider-dashboard-error">
        Could not load provider dashboard. Try refreshing.
      </div>
    );
  }
  if (!data) return <DashboardSkeleton />;

  const { profile, upcoming, earnings_30d, review_queue } = data;
  const upcomingSessions = data.upcoming_sessions?.sessions || [];
  const unreadThreads = data.unread_threads || 0;
  const payoutsReady = profile.stripe_payouts_enabled;
  const status = profile.status || "pending";
  const earnParts = [
    `${earnings_30d.booking_count} booking${earnings_30d.booking_count === 1 ? "" : "s"}`,
  ];
  if (earnings_30d.session_count > 0) earnParts.push(`${earnings_30d.session_count} session${earnings_30d.session_count === 1 ? "" : "s"}`);
  if (earnings_30d.course_count > 0) earnParts.push(`${earnings_30d.course_count} course sale${earnings_30d.course_count === 1 ? "" : "s"}`);

  return (
    <div className="space-y-8" data-testid="provider-dashboard-body">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
        <StatTile
          icon={CurrencyDollar}
          label="30-day net"
          value={fmtUsdRaw(earnings_30d.net_usd)}
          sub={earnParts.join(" · ")}
          tone="ok"
          testId="provider-stat-earnings"
        />
        <StatTile
          icon={ChartBar}
          label="Upcoming"
          value={upcoming.count + upcomingSessions.length}
          sub="Next 7 days"
          testId="provider-stat-upcoming"
        />
        <Link to="/inbox/threads" className="block" data-testid="provider-stat-messages-link">
          <StatTile
            icon={ChatCircleDots}
            label="Messages"
            value={unreadThreads}
            sub={unreadThreads > 0 ? "Unread — tap to reply" : "All caught up"}
            tone={unreadThreads > 0 ? "warn" : "default"}
            testId="provider-stat-messages"
          />
        </Link>
        <StatTile
          icon={Star}
          label="Rating"
          value={profile.rating_avg ? profile.rating_avg.toFixed(1) : "—"}
          sub={`${profile.reviews_count} review${profile.reviews_count === 1 ? "" : "s"}`}
          testId="provider-stat-rating"
        />
        <StatTile
          icon={ShieldCheck}
          label="Payouts"
          value={payoutsReady ? "Live" : "Setup"}
          sub={payoutsReady ? "Stripe verified" : "Action required"}
          tone={payoutsReady ? "ok" : "warn"}
          testId="provider-stat-payouts"
        />
      </div>

      {/* Status callout if approval/Stripe setup pending */}
      {(!payoutsReady || status !== "approved") && (
        <div
          className="rounded-2xl border border-[#C1272D]/25 bg-[#FDECEC] p-4 md:p-5 flex flex-col md:flex-row md:items-center justify-between gap-3"
          data-testid="provider-setup-callout"
        >
          <div className="flex items-start gap-3">
            <Warning size={18} weight="duotone" className="text-[#C1272D] flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-ink text-sm">Complete your setup</p>
              <p className="text-[13px] text-[#3F3F3F] leading-snug">
                {status !== "approved"
                  ? "Your application is still under review."
                  : "Connect Stripe to receive payouts on completed bookings."}
              </p>
            </div>
          </div>
          <Link to="/provider/me">
            <Button variant="outline" className="rounded-full border-[#C1272D]/30 text-[#C1272D] hover:bg-white whitespace-nowrap" data-testid="provider-setup-cta">
              Go to onboarding <ArrowRight size={13} className="ml-1.5" />
            </Button>
          </Link>
        </div>
      )}

      {/* Upcoming bookings strip */}
      <div className="rounded-2xl border border-[#EFEFEF] bg-white p-5 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-spectral text-xl text-ink">Upcoming bookings</h3>
          <Link to="/my-bookings" className="text-[13px] text-ink hover:text-crimson" data-testid="provider-view-all-bookings">
            View all →
          </Link>
        </div>
        {upcoming.bookings.length === 0 && upcomingSessions.length === 0 ? (
          <p className="text-[13px] text-[#6B6B6B]" data-testid="provider-no-upcoming">
            No bookings scheduled in the next 7 days. Share your profile link to drive new bookings.
          </p>
        ) : (
          <ul className="space-y-2">
            {upcomingSessions.map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between gap-3 py-2.5 border-b border-[#F0F0F0] last:border-0"
                data-testid={`provider-upcoming-session-${s.id}`}
              >
                <div className="min-w-0">
                  <p className="text-[13.5px] text-ink truncate inline-flex items-center gap-1.5">
                    <VideoCamera size={13} weight="duotone" className="text-[#7C3AED] flex-shrink-0" />
                    {s.tier_label}
                    <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#7C3AED] bg-[#7C3AED]/10 rounded-full px-2 py-0.5">Live session</span>
                  </p>
                  <p className="text-[11.5px] text-[#6B6B6B] truncate">
                    {new Date(s.scheduled_at).toLocaleString()}
                  </p>
                </div>
                <span className="font-tabular text-[13.5px] text-ink whitespace-nowrap">{fmtUsdRaw(s.amount)}</span>
              </li>
            ))}
            {upcoming.bookings.map((b) => (
              <li
                key={b.id}
                className="flex items-center justify-between gap-3 py-2.5 border-b border-[#F0F0F0] last:border-0"
                data-testid={`provider-upcoming-${b.id}`}
              >
                <div className="min-w-0">
                  <p className="text-[13.5px] text-ink truncate">{b.service_label || "Booking"}</p>
                  <p className="text-[11.5px] text-[#6B6B6B] truncate">
                    {b.customer_name || "Customer"} · {new Date(b.scheduled_at).toLocaleString()}
                  </p>
                </div>
                <span className="font-tabular text-[13.5px] text-ink whitespace-nowrap">{fmtUsdRaw(b.amount)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pending reviews CTA */}
      {review_queue.pending_count > 0 && (
        <div
          className="rounded-2xl border border-[#E8D9A6] bg-[#FBF7EC] p-4 md:p-5 flex flex-col md:flex-row md:items-center justify-between gap-3"
          data-testid="provider-review-callout"
        >
          <div className="flex items-start gap-3">
            <Star size={18} weight="duotone" className="text-gold-dark flex-shrink-0 mt-0.5" />
            <p className="text-[13.5px] text-ink">
              <strong>{review_queue.pending_count}</strong> recent booking{review_queue.pending_count === 1 ? "" : "s"} waiting on a customer review. Reviews lift your discovery ranking.
            </p>
          </div>
          <Link to="/my-bookings?tab=completed">
            <Button variant="outline" className="rounded-full border-[#E8D9A6] hover:bg-white whitespace-nowrap" data-testid="provider-review-cta">
              Send reminders
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Storefront body
// ---------------------------------------------------------------------
export function StorefrontDashboardBody() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get("/me/storefront-dashboard")
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setError(e?.response?.status === 404 ? "no_store" : "error"));
    return () => {
      alive = false;
    };
  }, []);

  if (error === "no_store") {
    return (
      <div
        className="rounded-2xl border border-[#E8D9A6] bg-[#FBF7EC] p-8 text-center"
        data-testid="storefront-empty-state"
      >
        <Storefront size={32} weight="duotone" className="mx-auto text-gold-dark mb-3" />
        <h3 className="font-spectral text-2xl text-ink mb-2">Open your Storefront</h3>
        <p className="text-[14px] text-[#3F3F3F] mb-5 max-w-md mx-auto">
          Sell food, merch, training, or any service from your own /store/&lt;slug&gt; — Stripe Tax + Connect ready.
        </p>
        <Link to="/me/store">
          <Button className="rounded-full bg-ink hover:bg-crimson text-white px-6 h-11" data-testid="storefront-open-cta">
            Open my Storefront <ArrowRight size={14} className="ml-1.5" />
          </Button>
        </Link>
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-sm text-[#C1272D]" data-testid="storefront-dashboard-error">
        Could not load storefront dashboard. Try refreshing.
      </div>
    );
  }
  if (!data) return <DashboardSkeleton />;

  const { store, orders_7d_count, revenue_30d_cents, top_product, low_stock, recent_orders } = data;

  return (
    <div className="space-y-8" data-testid="storefront-dashboard-body">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <StatTile
          icon={CurrencyDollar}
          label="30-day revenue"
          value={fmtUsd(revenue_30d_cents)}
          sub="Paid orders only"
          tone="ok"
          testId="storefront-stat-revenue"
        />
        <StatTile
          icon={Package}
          label="Orders this week"
          value={orders_7d_count}
          sub="Last 7 days"
          testId="storefront-stat-orders-7d"
        />
        <StatTile
          icon={Star}
          label="Top product"
          value={top_product ? (top_product.units || 0) : "—"}
          sub={top_product ? `${top_product.name?.slice(0, 22) || "—"} · ${fmtUsd(top_product.revenue_cents)}` : "No sales yet"}
          testId="storefront-stat-top-product"
        />
        <StatTile
          icon={Warning}
          label="Low stock"
          value={low_stock.count}
          sub={low_stock.count > 0 ? "Replenish soon" : "All good"}
          tone={low_stock.count > 0 ? "warn" : "ok"}
          testId="storefront-stat-low-stock"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent orders */}
        <div className="rounded-2xl border border-[#EFEFEF] bg-white p-5 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-spectral text-xl text-ink">Recent orders</h3>
            <Link
              to="/me/store?tab=orders"
              className="text-[13px] text-ink hover:text-crimson"
              data-testid="storefront-view-orders"
            >
              View all →
            </Link>
          </div>
          {recent_orders.length === 0 ? (
            <p className="text-[13px] text-[#6B6B6B]" data-testid="storefront-no-orders">
              No orders yet. Share your storefront link to drive your first sale.
            </p>
          ) : (
            <ul className="space-y-2">
              {recent_orders.map((o) => (
                <li
                  key={o.id}
                  className="flex items-center justify-between gap-3 py-2.5 border-b border-[#F0F0F0] last:border-0"
                  data-testid={`storefront-order-${o.id}`}
                >
                  <div className="min-w-0">
                    <p className="text-[13.5px] text-ink truncate">{o.product_name || "Order"}</p>
                    <p className="text-[11.5px] text-[#6B6B6B] truncate">
                      {o.customer_name || "Customer"} · {new Date(o.created_at).toLocaleDateString()} · {o.status}
                    </p>
                  </div>
                  <span className="font-tabular text-[13.5px] text-ink whitespace-nowrap">{fmtUsd(o.amount_cents)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Low stock list */}
        <div className="rounded-2xl border border-[#EFEFEF] bg-white p-5 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-spectral text-xl text-ink">Inventory alerts</h3>
            <Link
              to="/me/store?tab=products"
              className="text-[13px] text-ink hover:text-crimson"
              data-testid="storefront-view-products"
            >
              Manage products →
            </Link>
          </div>
          {low_stock.count === 0 ? (
            <div className="flex items-center gap-2 text-[#6B6B6B] text-[13px]" data-testid="storefront-stock-ok">
              <CheckCircle size={14} weight="duotone" className="text-emerald-600" />
              No products below the 3-unit threshold.
            </div>
          ) : (
            <ul className="space-y-2">
              {low_stock.items.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center justify-between gap-3 py-2.5 border-b border-[#F0F0F0] last:border-0"
                  data-testid={`storefront-low-${p.id}`}
                >
                  <p className="text-[13.5px] text-ink truncate">{p.name}</p>
                  <span className="font-tabular text-[12px] text-[#C1272D]">
                    {p.inventory_remaining} left
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl border border-[#0E0E0E] bg-ink text-white p-5 md:p-6">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-1">
            Your storefront
          </p>
          <p className="text-white text-[15px] font-medium">
            {store.name} · <span className="text-white/70">/store/{store.slug}</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/store/${store.slug}`} target="_blank" rel="noopener noreferrer">
            <Button className="rounded-full bg-white text-ink hover:bg-[#F4E5B0] h-10 px-5" data-testid="storefront-preview-cta">
              View public store
            </Button>
          </Link>
          <Link to="/me/store">
            <Button variant="outline" className="rounded-full border-white/30 text-white hover:bg-white/10 h-10 px-5" data-testid="storefront-edit-cta">
              Edit store
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
